import { PageOptions } from '@graphcommerce/framer-next-pages'
import {
  HygraphPagesQuery,
  PagesStaticPathsDocument,
  hygraphPageContent,
} from '@graphcommerce/graphcms-ui'
import { StoreConfigDocument, redirectOrNotFound } from '@graphcommerce/magento-store'
import {
  PageMeta,
  GetStaticProps,
  LayoutTitle,
  LayoutOverlayHeader,
  Row,
} from '@graphcommerce/next-ui'
import { Box } from '@mui/material'
import { GetStaticPaths } from 'next'
import {
  LayoutOverlay,
  LayoutOverlayProps,
  RowRenderer,
  LayoutNavigationProps,
} from '../../components'
import { SidebarMenu } from '../../components/GraphCMS/SidebarMenu/SidebarMenu'
import { graphqlSsrClient, graphqlSharedClient } from '../../lib/graphql/graphqlSsrClient'

type Props = HygraphPagesQuery
type RouteProps = { url: string[] }
type GetPageStaticPaths = GetStaticPaths<RouteProps>
type GetPageStaticProps = GetStaticProps<LayoutNavigationProps, Props, RouteProps>

function CustomerServiceOverlayPage({ pages }: Props) {
  const title = pages?.[0].title ?? ''

  return (
    <>
      <PageMeta
        title={title}
        metaDescription={title}
        canonical={pages?.[0]?.url ? `/${pages[0].url}` : undefined}
      />
      <LayoutOverlayHeader>
        <LayoutTitle component='span' size='small'>
          {title}
        </LayoutTitle>
      </LayoutOverlayHeader>
      <Box sx={{ display: { xs: 'block', md: 'flex' } }}>
        <SidebarMenu
          pages={pages}
          sx={(theme) => ({
            mt: theme.spacings.lg,
            ml: theme.spacings.sm,
            width: { xs: `calc(100% - 2 * ${theme.spacings.sm})`, md: '100%' },
            '& .MuiPaper-root': { background: theme.palette.background.default },
          })}
        />
        <Row
          sx={(theme) => ({
            '& > div': { p: '0 !important' },
            [theme.breakpoints.up('md')]: { width: '900px', maxWidth: 'calc(95vw - 300px)' },
          })}
        >
          <LayoutTitle
            sx={(theme) => ({ display: 'block', mb: `${theme.spacings.xxs} !important` })}
          >
            {title}
          </LayoutTitle>
          {pages?.[0] && <RowRenderer content={pages?.[0].content} />}
        </Row>
      </Box>
    </>
  )
}

const pageOptions: PageOptions<LayoutOverlayProps> = {
  overlayGroup: 'left',
  Layout: LayoutOverlay,
  layoutProps: { variantMd: 'left' },
}
CustomerServiceOverlayPage.pageOptions = pageOptions

export default CustomerServiceOverlayPage

// eslint-disable-next-line @typescript-eslint/require-await
export const getStaticPaths: GetPageStaticPaths = async ({ locales = [] }) => {
  if (process.env.NODE_ENV === 'development') return { paths: [], fallback: 'blocking' }

  const path = async (locale: string) => {
    const client = graphqlSharedClient(locale)
    const { data } = await client.query({
      query: PagesStaticPathsDocument,
      variables: {
        first: process.env.VERCEL_ENV !== 'production' ? 1 : 1000,
        urlStartsWith: 'klantenservice',
      },
    })
    return data.pages.map((page) => ({ params: { url: page.url.split('/').slice(1) }, locale }))
  }
  const paths = (await Promise.all(locales.map(path))).flat(1)

  return { paths, fallback: 'blocking' }
}

export const getStaticProps: GetPageStaticProps = async ({ locale, params }) => {
  const baseUrl = 'klantenservice'
  const url = params?.url ? `${baseUrl}/${params?.url.join('/')}` : `${baseUrl}`
  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)

  const conf = client.query({ query: StoreConfigDocument })
  const page = hygraphPageContent(staticClient, url, {
    rootCategory: (await conf).data.storeConfig?.root_category_uid ?? '',
  })

  if (!(await page).data.pages?.[0]) {
    return redirectOrNotFound(staticClient, conf, { ...params, url }, locale)
  }

  const isRoot = url === `${baseUrl}`

  return {
    props: {
      ...(await page).data,
      up: isRoot ? null : { href: `/${baseUrl}`, title: 'Terug' },
      apolloState: await conf.then(() => client.cache.extract()),
    },
    revalidate: 60 * 20,
  }
}
